import {
  ATTACHMENT_UPLOAD_CHUNK_BYTES,
  PROVIDER_SEND_TURN_MAX_FILE_BYTES,
  UploadedChatFileAttachment,
  type ProjectId,
  type ThreadId,
} from "@amoreg/contracts";
import * as Schema from "effect/Schema";
import { fileAttachmentMimeType } from "../components/chat/attachmentFiles";

export type UploadRequest = (path: string, init: RequestInit) => Promise<Response>;
const BASE = "/api/attachments/uploads";
const decodeReceipt = Schema.decodeUnknownSync(UploadedChatFileAttachment);
export async function cancelAttachmentUploads(
  request: UploadRequest,
  receipts: readonly UploadedChatFileAttachment[],
): Promise<void> {
  await Promise.allSettled(
    receipts.map((receipt) =>
      request(`${BASE}/cancel?id=${encodeURIComponent(receipt.id)}`, {
        method: "POST",
        headers: { "x-upload-token": receipt.uploadToken },
        signal: AbortSignal.timeout(10_000),
      }),
    ),
  );
}
const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

async function checkResponse(response: Response): Promise<void> {
  if (response.ok) return;
  const body = (await response.json().catch(() => null)) as { error?: unknown } | null;
  throw new Error(
    typeof body?.error === "string" ? body.error : `Attachment upload failed (${response.status}).`,
  );
}

/** Upload one chunk at a time. Retries resend the same offset; the server compares duplicate bytes. */
export async function uploadAttachments(input: {
  files: readonly File[];
  threadId: ThreadId;
  projectId: ProjectId;
  request: UploadRequest;
  signal: AbortSignal;
  onProgress?: (name: string, uploaded: number, total: number) => void;
}): Promise<UploadedChatFileAttachment[]> {
  const receipts: UploadedChatFileAttachment[] = [];
  try {
    for (const file of input.files) {
      input.signal.throwIfAborted();
      if (file.size <= 0 || file.size > PROVIDER_SEND_TURN_MAX_FILE_BYTES) {
        throw new Error(`'${file.name}' must be between 1 byte and 5 GB.`);
      }
      input.onProgress?.(file.name, 0, file.size);
      const response = await input.request(BASE, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          threadId: input.threadId,
          projectId: input.projectId,
          name: file.name,
          mimeType: fileAttachmentMimeType(file),
          sizeBytes: file.size,
        }),
        signal: AbortSignal.any([input.signal, AbortSignal.timeout(120_000)]),
      });
      await checkResponse(response);
      const receipt = decodeReceipt(await response.json());
      if (receipt.sizeBytes !== file.size)
        throw new Error("The server returned an invalid upload size.");
      receipts.push(receipt);
      for (let offset = 0; offset < file.size; offset += ATTACHMENT_UPLOAD_CHUNK_BYTES) {
        const end = Math.min(offset + ATTACHMENT_UPLOAD_CHUNK_BYTES, file.size);
        const body = file.slice(offset, end);
        for (let attempt = 0; ; attempt += 1) {
          input.signal.throwIfAborted();
          let chunk: Response;
          try {
            chunk = await input.request(
              `${BASE}/chunk?id=${encodeURIComponent(receipt.id)}&offset=${offset}`,
              {
                method: "POST",
                headers: {
                  "content-type": "application/octet-stream",
                  "x-upload-token": receipt.uploadToken,
                },
                body,
                signal: AbortSignal.any([input.signal, AbortSignal.timeout(120_000)]),
              },
            );
          } catch (error) {
            if (input.signal.aborted || attempt >= 2) throw error;
            await delay(500 * (attempt + 1));
            continue;
          }
          if (
            (chunk.status >= 500 || chunk.status === 408 || chunk.status === 429) &&
            attempt < 2
          ) {
            await chunk.body?.cancel();
            await delay(500 * (attempt + 1));
            continue;
          }
          await checkResponse(chunk);
          let result: { offset?: unknown };
          try {
            result = (await chunk.json()) as { offset?: unknown };
          } catch (error) {
            if (input.signal.aborted || attempt >= 2) throw error;
            await delay(500 * (attempt + 1));
            continue;
          }
          if (result.offset !== end)
            throw new Error("The server did not confirm the uploaded chunk.");
          break;
        }
        input.onProgress?.(file.name, end, file.size);
      }
    }
    return receipts;
  } catch (error) {
    // Use a fresh signal: the user's cancellation must not cancel cleanup itself.
    await cancelAttachmentUploads(input.request, receipts);
    if (input.signal.aborted)
      throw new Error("Upload cancelled. Your files are still in the draft.");
    throw error;
  }
}
